import { useState } from "react";

import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { ISO_CODE_TO_COUNTRY_NAME } from "@/core/constants";
import Danger from "@/images/Danger";
import ChevronComponent from "@/tpo/Chevron";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

import StripeCalloutBanner from "./StripeCalloutBanner";

export default function YourLinkedBankAccount({ missingRequirements = [] }) {
  const { organisation } = useOrganisationContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const address =
    (!organisation.stripeConnectedAccountAddress && {}) ||
    JSON.parse(organisation.stripeConnectedAccountAddress);

  const hasIssues = missingRequirements.length > 0;

  return (
    <>
      {hasIssues ? (
        <Stack gap={20}>
          <Group alignItems="center" gap={10}>
            <Danger size={36} />
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              Additional details required
            </Box>
          </Group>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight={1.5}>
            It looks like additional details are required to complete your setup:
          </Box>
          <Box as="ul" pl={20} fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
            {missingRequirements.map((requirement, index) => (
              <Box as="li" key={index} lineHeight={1.8}>
                {requirement}
              </Box>
            ))}
          </Box>
        </Stack>
      ) : (
        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
            Your linked bank account
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
            You have successfully verified your business account and linked your payment details. If
            you need to update any details please edit below
          </Box>
        </Stack>
      )}
      <Stack gap={[40]}>
        <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]}>
          Business address
        </Box>
        <Stack gap={20}>
          {!!organisation?.stripeConnectedAccountName && (
            <FloatingLabelInput
              label="Business name"
              value={organisation.stripeConnectedAccountName}
              editable={false}
            />
          )}
          {!!address?.line1 && (
            <FloatingLabelInput
              label="Address line 1"
              value={address.line1}
              editable={false}
            />
          )}
          {!!address?.line2 && (
            <FloatingLabelInput
              label="Address line 2"
              value={address.line2}
              editable={false}
            />
          )}
          {!!address?.city && (
            <FloatingLabelInput label="City" value={address.city} editable={false} />
          )}
          {!!address?.postal_code && (
            <FloatingLabelInput
              label="Postcode"
              value={address.postal_code}
              editable={false}
            />
          )}
          {!!address?.country && (
            <FloatingLabelInput
              label="Country"
              value={ISO_CODE_TO_COUNTRY_NAME[address.country] || ""}
              editable={false}
            />
          )}
        </Stack>
      </Stack>
      <Stack gap={[40]}>
        <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]}>
          Business details
        </Box>
        <Stack gap={20}>
          {!!organisation?.website && (
            <FloatingLabelInput label="Website" value={organisation.website} editable={false} />
          )}
          {!!organisation?.vatNumber && (
            <FloatingLabelInput
              label="VAT number"
              value={organisation.vatNumber}
              editable={false}
            />
          )}
          {!!organisation?.companyNumber && (
            <FloatingLabelInput
              label="Company number"
              value={organisation.companyNumber}
              editable={false}
            />
          )}
          {!!organisation?.contactNumber && (
            <FloatingLabelInput
              label="Contact number"
              value={organisation.contactNumber}
              editable={false}
            />
          )}
        </Stack>
      </Stack>
      <Stack gap={40}>
        <StripeCalloutBanner fontSize={14} />
        <ButtonV2
          cursor="pointer"
          as={ExternalTextLink}
          underline={false}
          target="_self"
          rel="noopener noreferrer"
          mx="auto"
          color="green"
          rightIcon={<ChevronComponent />}
          onClick={() => setButtonDisabled(true)}
          disabled={buttonDisabled}
          type="button"
          size={["sm", "sm", "md"]}
          href={organisation?.onboardingUrl}
        >
          {hasIssues ? "Complete Setup" : "edit"}
        </ButtonV2>
      </Stack>
    </>
  );
}
